<template>
  <v-card class="base-card pa-4">
    <v-card-title class="d-flex justify-end pa-0 pb-6">
      <div class="d-flex">
        <v-icon
          v-show="isAdmin && event.id"
          v-if="isShowPublishEvent"
          class="ml-4 uikit-icon check-circle success-h"
          title="Опубликовать"
          :disabled="toPublishEventLoading"
          @click="toPublishEvent"
        />

        <v-icon
          v-show="isAdmin && event.id"
          class="ml-4 uikit-icon primary-h"
          :title="isEdit ? 'Закрыть' : 'Редактировать'"
          :class="isEdit ? 'close' : 'edit-icon'"
          @click="switchEdit"
        />
      </div>
    </v-card-title>

    <UIProgressCircular v-if="!event.id" />

    <v-card-text
      v-show="event.id"
      class="px-0"
    >
      <v-list
        ref="list"
        class="px-4 py-0 transparent overflow-auto"
      >
        <template v-if="!isEdit">
          <SimpleTextRow
            name="Название мероприятия:"
            :value="event.name"
          />
          <SimpleTextRow
            name="Статус:"
            :value="event.status ? event.status.name : 'Черновик'"
          />
          <SimpleTextRow
            name="Время начала:"
            :value="event.dateTimeFromText"
          />
          <SimpleTextRow
            name="Время окончания:"
            :value="event.dateTimeToText"
          />
          <SimpleTextRow
            name="Адрес:"
            :value="event.address ? event.address : ''"
          />
          <SimpleTextRow
            name="Контактные данные:"
            :value="event.contactData"
          />
          <SimpleTextRow
            name="Действия:"
            :value="actionsText"
          />
          <SimpleTextRow
            name="Мероприятие скрыто:"
            :value="getBooleanTextValue(event.isHidden)"
          />
          <SimpleTextRow
            name="Регистрация закрыта:"
            :value="getBooleanTextValue(event.isClosed)"
            hide-border-bottom
          />
        </template>

        <template v-else>
          <UITextField
            v-model="newEvent.name"
            label="Название"
            :error-messages="errorMessages.name"
            @input="$v.newEvent.name.$touch()"
          />

          <UITextField
            v-model="newEvent.address"
            label="Адрес"
            :error-messages="errorMessages.address"
            @input="$v.newEvent.address.$touch()"
          />

          <UIAutocomplete
            v-model="newEvent.action"
            label="Действия"
            multiple
            return-object
            :items="actionDirectory"
            :error-messages="errorMessages.action"
            @input="$v.newEvent.action.$touch()"
          />

          <UITextField
            v-model="newEvent.contactData"
            label="Контактные данные"
            :error-messages="errorMessages.contactData"
            @input="$v.newEvent.contactData.$touch()"
          />

          <v-row class="my-0">
            <v-col
              :cols="isXs ? 12 : 6"
              class="py-0"
            >
              <UIDatePickerMenu
                v-model="newEvent.dateFrom"
                label="Дата начала"
                :error-messages="errorMessages.dateFrom"
                @input="$v.newEvent.dateFrom.$touch()"
              />
            </v-col>

            <v-col
              :cols="isXs ? 12 : 6"
              class="py-0"
            >
              <UITextField
                v-model="newEvent.timeFrom"
                v-mask="'##:##'"
                label="Время начала"
                :error-messages="errorMessages.timeFrom"
                @input="$v.newEvent.timeFrom.$touch()"
              />
            </v-col>
          </v-row>

          <v-row class="my-0">
            <v-col
              :cols="isXs ? 12 : 6"
              class="py-0"
            >
              <UIDatePickerMenu
                v-model="newEvent.dateTo"
                label="Дата окончания"
                :error-messages="errorMessages.dateTo"
                @input="$v.newEvent.dateTo.$touch()"
              />
            </v-col>

            <v-col
              :cols="isXs ? 12 : 6"
              class="py-0"
            >
              <UITextField
                v-model="newEvent.timeTo"
                v-mask="'##:##'"
                label="Время окончания"
                :error-messages="errorMessages.timeTo"
                @input="$v.newEvent.timeTo.$touch()"
              />
            </v-col>
          </v-row>

          <v-row class="my-0">
            <v-col
              cols="12"
              class="py-0 pr-1"
            >
              <v-checkbox
                v-model="newEvent.isHidden"
                label="Скрыть мероприятие"
                class="mt-0 base-check-box"
                hide-details
              />
            </v-col>
          </v-row>

          <v-row class="my-0 mt-2">
            <v-col
              cols="12"
              class="py-0 pr-1"
            >
              <v-checkbox
                v-model="newEvent.isClosed"
                label="Закрыть регистрацию на мероприятие"
                class="mt-0 base-check-box"
                hide-details
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-8">
            <v-btn
              outlined
              small
              color="primary"
              height="32"
              class="action-btn elevation-0 rounded-lg"
              :loading="saveBtnLoading"
              :disabled="$v.$invalid"
              @click="saveEvent"
            >
              <v-icon
                class="uikit-icon check primary size-20"
                :class="$v.$invalid ? 'greylighten1' : 'primary'"
              />
              <span class="ml-1 btn-text">Сохранить</span>
            </v-btn>
          </div>
        </template>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  setValidateMessages,
  dateFormat,
  checkTimeFormat,
} from '@/scripts/validation'
import { Event } from '@/models'
import {
  maxLength,
  required,
} from 'vuelidate/lib/validators'
import {
  mapActions,
  mapGetters,
} from 'vuex'
import { eventApi } from '@/api'
import SimpleTextRow from '@/ui/row/SimpleTextRow'
import UITextField from '@/ui/input/UITextField'
import UIDatePickerMenu from '@/ui/input/UIDatePickerMenu'
import UIAutocomplete from '@/ui/input/UIAutocomplete'
import UIProgressCircular from '@/ui/progress/UIProgressCircular'
import { FormTemplateStatus } from '@/enums'
import { getBooleanTextValue } from '@/scripts/checkbox'
import _ from 'lodash'

export default {
  components: {
    UIProgressCircular,
    UIAutocomplete,
    UIDatePickerMenu,
    UITextField,
    SimpleTextRow,
  },

  props: {
    formTemplateList: {
      type: Array,
      default: null,
    },
    currentEvent: Event,
  },

  async mounted() {
    await this.getDirectories()
    this.setListHeight()
    window.addEventListener('resize', this.setListHeight)
  },

  destroyed() {
    window.removeEventListener('resize', this.setListHeight)
  },

  data: () => ({
    FormTemplateStatus,

    event: new Event(),
    newEvent: new Event(),

    isEdit: false,

    toPublishEventLoading: false,
    saveBtnLoading: false,
  }),

  validations() {
    return {
      newEvent: {
        name: { required, maxLength: maxLength(200) },
        dateFrom: { required, dateFormat },
        dateTo: { required, dateFormat },
        timeFrom: { required, checkTimeFormat },
        timeTo: { required, checkTimeFormat },
        address: { required },
        action: { required },
        contactData: { maxLength: maxLength(200) },
      },
    }
  },

  computed: {
    ...mapGetters('directories', ['actionDirectory', 'statusDirectory']),
    ...mapGetters('user', ['isAdmin']),
    ...mapGetters('breakpoint', ['isXs']),

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.newEvent.$params).forEach((key) => {
        allErrors[key] = setValidateMessages(this.$v.newEvent[key])
      })

      return allErrors
    },

    isShowPublishEvent() {
      return this?.formTemplateList.length
        && (!this.event.status || (this.event.status && this.event.status.id === FormTemplateStatus.DRAFT))
    },

    actionsText() {
      return this.event.action ? this.event.action.map((el) => (el ? el.name : 'Название не найдено')).join(', ')
        : ''
    },
  },

  methods: {
    getBooleanTextValue,
    ...mapActions('directories', ['getActionDirectory']),

    switchEdit() {
      this.isEdit = !this.isEdit
      if (this.isEdit) this.newEvent = new Event(this.event.getForAPI())
    },

    async getDirectories() {
      try {
        await this.getActionDirectory()
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка получения справочников')
      }
    },

    async saveEvent() {
      try {
        this.saveBtnLoading = true
        const data = await eventApi.update(this.newEvent.getForAPI())

        this.event = new Event(data)

        this.$toastr('success', '', 'Мероприятие успешно сохранено')
        this.switchEdit()
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    async toPublishEvent() {
      try {
        this.toPublishEventLoading = true
        const data = await eventApi.publish(this.event.getForAPI())
        this.event = new Event(data)
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка публикации')
      } finally {
        this.toPublishEventLoading = false
      }
    },

    setListHeight() {
      this.$nextTick(() => {
        if (this.$refs?.list) {
          const footerHeight = 67
          const topHeight = this.$refs?.list.$el.getBoundingClientRect().top

          this.$refs.list.$el.style.height = `${window.innerHeight - topHeight - footerHeight}px`
        }
      })
    },
  },

  watch: {
    currentEvent: {
      immediate: true,
      deep: true,
      handler() {
        this.event = _.cloneDeep(this.currentEvent)
      },
    },

    'event.id': {
      handler() {
        this.setListHeight()
      },
    },
  },
}
</script>
